<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue';
import { getPrefixedUuid } from '../utils';
import { DatePickerProps } from '../types';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import FormInputDisplay from '../display/FormInputDisplay.vue';
import { DateTime } from 'luxon';
import { useToggle } from '@vueuse/core';
import { mdiCalendar } from '@mdi/js';
import TextField from '../TextField/TextField.vue';
import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { useTranslation } from 'i18next-vue';

const { t } = useTranslation();

const maskOptions = maskitoDateOptionsGenerator({
  mode: 'yyyy/mm/dd',
  separator: '-',
});

const id = getPrefixedUuid('datePicker');

const props = withDefaults(defineProps<DatePickerProps>(), {
  mode: 'edit',
});

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  placeholder: t('YearMonthDay'),
}));

const attrs = useAttrs();

const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const emits = defineEmits<{
  'update:modelValue': [dateTime: DateTime | undefined];
}>();

const [pickerOpen, togglePicker] = useToggle(false);

const dateFormat = 'yyyy-MM-dd';

const datePickerModelValue = ref<Date | undefined>();
const textInputModelValue = ref<string | undefined>();

watch(
  () => props.modelValue,
  (modelValue) => {
    // VDatePicker reads the native Date in the browser-local zone, while modelValue
    // lives in the user-preference zone (Settings.defaultZone). Using toJSDate() would
    // shift the displayed day when the two zones differ.
    datePickerModelValue.value = modelValue?.isValid
      ? // The reason for month - 1 is because Luxon to JSDate conversion. Building picker value.
        new Date(modelValue.year, modelValue.month - 1, modelValue.day)
      : undefined;

    textInputModelValue.value = modelValue?.isValid ? modelValue.toFormat(dateFormat) : undefined;
  },
  {
    immediate: true,
  },
);

function inputUpdate(input: string | undefined) {
  const newDate = input ? DateTime.fromFormat(input, dateFormat) : undefined;
  if (newDate?.isValid) {
    onUpdate(newDate);
    return;
  }

  // ponytail: mask skeleton with no digits counts as cleared; otherwise keep last valid
  if (!input || !/\d/.test(input)) {
    onUpdate(undefined);
  }
}

function pickerUpdate(input: Date | undefined) {
  if (input) {
    const newDate = DateTime.fromObject({
      year: input.getFullYear(),
      // The reason for month + 1 is JSDate to Luxon DateTime conversion. Reading picker selection.
      month: input.getMonth() + 1,
      day: input.getDate(),
    });

    onUpdate(newDate);
  } else {
    throw new Error(`DatePickerSelection ${input} invalid`);
  }
}

function onUpdate(date: DateTime | undefined) {
  if (date?.isValid) {
    const { year, month, day } = date;

    // if `modelValue`  has been defined initially, we use the time-information from there, otherwise we sill set it to "startOfTheDay"
    const newDate = props.modelValue
      ? props.modelValue.set({ year, month, day })
      : DateTime.fromObject({
          year,
          month,
          day,
          hour: 0,
          minute: 0,
        });

    emits('update:modelValue', newDate.isValid ? newDate : undefined);
  } else {
    emits('update:modelValue', undefined);
  }
}
</script>

<template>
  <div :class="$attrs.class">
    <FormLabel :for="id" :infoMessage="infoMessage" :label="label" :disabled="props.disabled">
      <template #default="{ hoverProps }">
        <FormInputDisplay v-if="mode === 'details'" :value="textInputModelValue" />
        <TextField
          v-else
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps, id }"
          v-model="textInputModelValue"
          v-maskito="maskOptions"
          :data-testId="$attrs['data-testid'] ? $attrs['data-testid'] : `${label}_Date`"
          @update:modelValue="inputUpdate"
        >
          <template #prepend-inner>
            <v-menu
              v-model="pickerOpen"
              :closeOnContentClick="false"
              transition="scale-transition"
              location="left"
            >
              <template #activator="{ props: activatorProps }">
                <v-btn
                  variant="text"
                  density="compact"
                  color="secondary"
                  v-bind="activatorProps"
                  :icon="mdiCalendar"
                  @click="togglePicker"
                />
              </template>

              <v-date-picker
                :modelValue="datePickerModelValue"
                title=""
                color="secondary"
                @update:modelValue="pickerUpdate"
              />
            </v-menu>
          </template>
        </TextField>
      </template>
    </FormLabel>
  </div>
</template>
